<script setup lang="ts">
import { api } from '@/api';
import {
  type CreateUpdateTextDto,
  EntityOrigin,
  EntityWithCommentType,
  ReferencingObjectDto,
  type TextDto,
} from '@/api/generator';
import Comments from '@/components/comments/Comments.vue';
import { useAmountComments } from '@/components/comments/useAmountComments';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import type { EntityInfo } from '@/components/confirmationModals/types';
import EntityChangeBlock from '@/components/EntityHistory/EntityChangeBlock.vue';
import EntityReferencesModal from '@/components/EntityReferencesModal/EntityReferencesModal.vue';
import VeeAttachments from '@/components/forms/Attachments/VeeAttachments.vue';
import VeeAvailability from '@/components/forms/Availability/VeeAvailability.vue';
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import FormSection from '@/components/forms/FormSection/FormSection.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import { useFormTab } from '@/components/forms/useFormTab';
import HistoryEvents from '@/components/history/HistoryEvents.vue';
import { mainNavigationMedicalSystems, mainNavigationQaEquipment } from '@/components/icons/icons';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import { defaultPageTabs, getDefaultPageActions } from '@/components/pages/BasePage/defaults';
import type { PageAction } from '@/components/pages/BasePage/types';
import { useScopeOptions } from '@/components/quantitiesAndUnits/quantitiesAndUnitsUtils';
import { type TabDefinition } from '@/components/Tabs/types.ts';
import { routenames } from '@/router/routenames';
import { translateEntityProperty } from '@/utils/helper.ts';
import ScopeGroups from '@/views/enumerations/components/ScopeGroups.vue';
import ScopeList from '@/views/enumerations/components/ScopeList.vue';
import GreenBoxHolder from '@/views/quantities/components/GreenBoxHolder.vue';
import { useTranslation } from 'i18next-vue';
import { useFormValues } from 'vee-validate';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useDeleteTextWithConfirmation } from '../utils';

const { t } = useTranslation();

type Text = TextDto & CreateUpdateTextDto;

const values = useFormValues<Text>();

const mode = useFormMode();
const { action: deleteAction } = useDeleteTextWithConfirmation();

const initialUseInSetup = Boolean(values.value?.isUseInSetupParameter);

const isSetupUsageEnabled = computed(() => values.value.isUseInSetupParameter ?? false);

const { commentsChanged, commentsTabTitle } = useAmountComments(values);

const isPredefined = computed(() => values.value.origin === EntityOrigin.Predefined);
const isInEditMode = mode.value === 'edit';
const isInAddMode = mode.value === 'add';
const isInDetailsMode = mode.value === 'details';

const isReferenced = computed(() => values.value.isReferenced || false);
const isDisabled = computed(() => !isSetupUsageEnabled.value && (isInEditMode || isInAddMode));

const { getIndicatorState } = useFormTab();

const tabs = computed(
  () =>
    ({
      administrative: {
        ...defaultPageTabs.administrative,
        title: t('AdministrativeInformation'),
        indicatorState: getIndicatorState(['name', 'description', 'attachments']),
      },
      useInSetups: {
        title: t('UseInSetup_other'),
        hideContentSheet: true,
        indicatorState: getIndicatorState(['isUseInSetupParameter', 'setupScope']),
      },
      comments: {
        ...defaultPageTabs.comments,
        hidden: isInAddMode,
        title: commentsTabTitle.value,
      },

      history: {
        ...defaultPageTabs.history,
        hidden: defaultPageTabs.history.hidden || isInAddMode,
        unmountTab: true,
      },
    }) satisfies TabDefinition,
);

const router = useRouter();

const actions = computed<PageAction[]>(() => {
  const defaultPageActions = getDefaultPageActions(mode.value, values.value);

  return [
    {
      ...defaultPageActions.edit,
      to: {
        name: routenames.texts.edit,
        params: { id: values.value?.id },
      },
    },
    {
      ...defaultPageActions.delete,
      callback: async () => {
        if (values.value.id) {
          const { result } = await deleteAction({
            param: values.value.id,
            confirmationOptions: getDefaultDeleteConfirmationOptions(
              {
                ...(values.value as EntityInfo),
                typeName: 'Text_one',
              },
              t,
            ),
          });
          if (result?.success) {
            router.push({
              name: routenames.texts.overview,
            });
          }
        }
      },
    },
  ];
});

const entityReferences = ref<ReferencingObjectDto[] | null>(null);

const { load, options } = useScopeOptions();

onMounted(load);
</script>

<template>
  <BasePageNavigation :tabs="tabs" :actions="actions">
    <!-- ADMINISTRATIVE -->
    <template #administrative>
      <FormSection>
        <v-row class="justify-sm-start">
          <v-col cols="5">
            <VeeTextField
              :label="$t('Name')"
              name="name"
              :mode="isPredefined ? 'details' : undefined"
              :placeholder="$t('Required')"
              :outputConverter="() => translateEntityProperty('name', values)"
            />
          </v-col>

          <v-col cols="5">
            <VeeTextarea
              :label="$t('Description')"
              name="description"
              :mode="isPredefined ? 'details' : undefined"
              :outputConverter="() => translateEntityProperty('description', values)"
            />
            <VeeAttachments class="mt-1" name="attachments" data-testid="Description_Attachments" />
          </v-col>

          <v-col cols="2">
            <VeeAvailability :disabled="mode !== 'details'" name="isAvailable" />
            <EntityChangeBlock v-if="isInDetailsMode" :entity="values" />
          </v-col>
        </v-row>
      </FormSection>
    </template>

    <!-- SETUPS -->
    <template #useInSetups>
      <div class="d-flex flex-column ga-6">
        <InlineNotification
          v-if="(isReferenced || isPredefined) && isInEditMode"
          variant="flat"
          :value="{
            type: 'info',
            message: $t('FieldsCanNotBeEditedBecauseOfReferenceOfEntityMsg', {
              entity: $t('Text_one'),
            }),
          }"
        >
          <template #append>
            <span
              v-if="values.isReferenced"
              class="ml-2 linkButton"
              @click.stop="() => (entityReferences = values.isReferencedBy ?? null)"
              >{{ $t('Info') }}</span
            >
          </template>
        </InlineNotification>

        <GreenBoxHolder v-if="!isInDetailsMode" :active="!!values?.isUseInSetupParameter">
          <VeeCheckbox
            :hideDetails="true"
            :noValidationOnCheck="true"
            :disabled="isInEditMode && initialUseInSetup && (isReferenced || isPredefined)"
            name="isUseInSetupParameter"
            :label="$t('UseInSetup_other')"
          />
        </GreenBoxHolder>

        <template v-if="!values?.isUseInSetupParameter && isInDetailsMode">
          <p class="text-body-1 font-weight-bold text-primary-lighten-2">
            {{ $t('TextNotUsedInSetupsMsg') }}
          </p>
        </template>

        <FormSection
          v-else
          :disabled="!values.isUseInSetupParameter"
          data-testid="formSection_UseInSetupCategory"
        >
          <v-card class="container-level-1">
            <v-card-item
              ><v-card-title>
                <h5>{{ $t('UseInSetupCategory_other') }}</h5>
              </v-card-title></v-card-item
            >
            <v-card-text>
              <!-- medical systems and QA equipment share one `setupScope` list -->
              <v-row>
                <v-col cols="12">
                  <v-label>
                    <v-icon
                      size="40"
                      class="mr-2 text-primary-darken-2"
                      :icon="mainNavigationMedicalSystems"
                    />
                    <h6 class="text-primary-darken-2">
                      {{ $t('MedicalSystems') }}
                    </h6>
                  </v-label>
                  <ScopeGroups
                    name="setupScope"
                    :scopeOptions="options.medicalSystems"
                    scopeKey="rtSystemSubCategory"
                    groupKey="rtSystemCategory"
                    :disableSettedOptions="
                      (isReferenced || isPredefined) && values.isUseInSetupParameter
                    "
                    :disabled="isDisabled"
                  />
                </v-col>
              </v-row>
              <v-divider class="my-6" />
              <v-row>
                <v-col cols="12">
                  <v-label>
                    <v-icon
                      size="40"
                      class="mr-2 text-primary-darken-2"
                      :icon="mainNavigationQaEquipment"
                    />
                    <h6 class="text-primary-darken-2">
                      {{ $t('DosimetryAndQaEquipment') }}
                    </h6>
                  </v-label>
                  <ScopeList
                    name="setupScope"
                    :scopeOptions="options.qaEquipment"
                    scopeKey="rtSystemSubCategory"
                    :disableSettedOptions="
                      (isReferenced || isPredefined) && values.isUseInSetupParameter
                    "
                    :disabled="isDisabled"
                  />
                </v-col> </v-row
            ></v-card-text>
          </v-card>
        </FormSection>
      </div>
    </template>

    <template #comments>
      <FormSection data-testid="formSection_Comments">
        <Comments
          v-if="values.id"
          :id="values.id"
          :entityWithCommentType="EntityWithCommentType.Text"
          @change="commentsChanged"
        />
      </FormSection>
    </template>

    <template #history>
      <HistoryEvents
        v-if="values.id"
        :id="values.id"
        :historyApiFn="api.textsApi.getTextHistory.bind(api.textsApi)"
      />
    </template>
  </BasePageNavigation>
  <EntityReferencesModal
    v-if="values.isReferenced"
    :references="entityReferences"
    :title="$t('ReferenceingEntities')"
    @dialogOpen="
      (isOpen: boolean) => {
        if (!isOpen) entityReferences = null;
      }
    "
  ></EntityReferencesModal>
</template>
